import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCategories, submitFactSuggestion } from "@/lib/api";
import { getCategoryProgress } from "@/lib/progress";
import { getTestProgress } from "@/lib/testProgress";
import { CATEGORY_GROUPS, CATEGORY_GROUP_BY_ID, getCategoryGroup } from "@/lib/categoryGroups";
import Header from "@/components/Header";
import { ArrowUpRight, BookOpenCheck, Sparkles, BookOpen, ListChecks, RotateCcw, Layers } from "lucide-react";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState({
    category_id: "",
    text: "",
    is_true: true,
  });
  const [suggestionStatus, setSuggestionStatus] = useState("");
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (e) {
        setError("Kategoriler yüklenemedi. Bağlantınızı kontrol edin.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submitSuggestion = async (e) => {
    e.preventDefault();
    setSuggestionStatus("");
    if (!suggestion.category_id || !suggestion.text.trim()) {
      setSuggestionStatus("Kategori ve önerme metni zorunludur.");
      return;
    }
    setSuggestionLoading(true);
    try {
      await submitFactSuggestion({
        category_id: suggestion.category_id,
        text: suggestion.text.trim(),
        is_true: suggestion.is_true,
      });
      setSuggestion({ category_id: suggestion.category_id, text: "", is_true: true });
      setSuggestionStatus("Öneriniz alındı. Admin onayından sonra kart havuzuna eklenebilir.");
    } catch {
      setSuggestionStatus("Öneri gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setSuggestionLoading(false);
    }
  };

  const suggestionBox = !loading && !error && categories.length > 0 && (
    <section className="mt-16 rounded-2xl border border-[#e8eae3] bg-white p-6 md:p-7">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="font-serif-fact text-2xl text-[#1c2723]">
            Kart Havuzuna Önerme Kat
          </h2>
          <p className="text-sm text-[#5c635e] mt-1">
            Bir kategori seç, doğru ya da yanlış bir önerme gönder. Admin uygun bulursa kart havuzuna eklenir.
          </p>
        </div>
      </div>
      <form onSubmit={submitSuggestion} className="grid md:grid-cols-[1fr_1.8fr_auto] gap-3 items-start">
        <select
          value={suggestion.category_id}
          onChange={(e) => setSuggestion((s) => ({ ...s, category_id: e.target.value }))}
          className="h-11 rounded-xl border border-[#d6d9ce] bg-[#fdfdfb] px-3 text-sm text-[#1c2723]"
        >
          <option value="">Kategori seç</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
        <div className="space-y-2">
          <textarea
            value={suggestion.text}
            onChange={(e) => setSuggestion((s) => ({ ...s, text: e.target.value }))}
            rows={3}
            placeholder="Örn: Fiil ehliyeti için ayırt etme gücü şarttır."
            className="w-full rounded-xl border border-[#d6d9ce] bg-[#fdfdfb] px-3 py-2 text-sm text-[#1c2723] resize-none"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSuggestion((s) => ({ ...s, is_true: true }))}
              className={`h-9 px-4 rounded-full text-sm ${suggestion.is_true ? "bg-[#2e4733] text-white" : "border border-[#d6d9ce] text-[#5c635e]"}`}
            >
              Doğru önerme
            </button>
            <button
              type="button"
              onClick={() => setSuggestion((s) => ({ ...s, is_true: false }))}
              className={`h-9 px-4 rounded-full text-sm ${!suggestion.is_true ? "bg-[#a55e3f] text-white" : "border border-[#d6d9ce] text-[#5c635e]"}`}
            >
              Yanlış önerme
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={suggestionLoading}
          className="h-11 px-5 rounded-full bg-[#2e4733] text-[#f5f6f2] hover:bg-[#1c2723] transition-colors text-sm font-medium disabled:opacity-50"
        >
          {suggestionLoading ? "Gönderiliyor..." : "Öner"}
        </button>
      </form>
      {suggestionStatus && (
        <div className="mt-3 text-sm text-[#5c635e]">{suggestionStatus}</div>
      )}
    </section>
  );

  const groupedCategories = CATEGORY_GROUPS.map((group) => {
    const groupCategories = categories.filter((category) => getCategoryGroup(category) === group.id);
    const factCount = groupCategories.reduce((sum, category) => sum + (category.fact_count || 0), 0);
    return { ...group, categories: groupCategories, factCount };
  });
  const selectedGroup = selectedGroupId ? CATEGORY_GROUP_BY_ID[selectedGroupId] : null;
  const selectedGroupCategories = selectedGroupId
    ? categories.filter((category) => getCategoryGroup(category) === selectedGroupId)
    : [];

  return (
    <div className="min-h-screen bg-paper bg-grain relative">
      <Header />

      <main className="relative z-10 max-w-6xl mx-auto px-4 md:px-2 pt-16 md:pt-24 pb-24">
        {/* Hero */}
        <section className="grid md:grid-cols-12 gap-10 items-end mb-20">
          <div className="md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-1.5 w-8 rounded-full bg-[#4a6741]" />
              <span className="label-eyebrow">Sınava hazırlık · tek cümle yöntemi</span>
            </div>
            <h1 className="font-serif-fact text-[#1c2723] text-4xl md:text-5xl lg:text-[3.6rem] leading-[1.08] tracking-tight">
              Tek cümle.
              <br />
              Tek bilgi.
              <br />
              <span className="italic text-[#4a6741]">Sonra bir sonraki.</span>
            </h1>
            <p className="mt-8 text-[#5c635e] text-base md:text-lg max-w-xl leading-relaxed">
              Sınav konularını ezbere çalışmak yerine, her bilgiyi tek başına okuyun.
              Hazır olduğunuzda <em className="text-[#1c2723] not-italic font-medium">Öğrendim</em> deyin —
              kart kendini geri çekecek, yenisi yerini alacak.
            </p>
          </div>
          <div className="md:col-span-4">
            <div className="rounded-2xl border border-[#e8eae3] bg-[#ffffff]/60 backdrop-blur-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={14} className="text-[#4a6741]" />
                <span className="label-eyebrow">Nasıl çalışır</span>
              </div>
              <ol className="space-y-3 text-sm text-[#1c2723]">
                <li className="flex gap-3">
                  <span className="font-mono-key text-[#a3aba6]">01</span>
                  <span>
                    <em className="not-italic font-medium">Çalış</em>: doğru bilgileri tek tek oku.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono-key text-[#a3aba6]">02</span>
                  <span>
                    <em className="not-italic font-medium">Test ol</em>: kategori havuzundan rastgele 100 soru gelir.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono-key text-[#a3aba6]">03</span>
                  <span>Skorun ve ilerlemen tarayıcıda saklanır.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono-key text-[#a3aba6]">04</span>
                  <span>Klavye kısayollarıyla daha hızlı çalış.</span>
                </li>
              </ol>
            </div>
          </div>
        </section>

        {false && !loading && !error && categories.length > 0 && (
          <section className="mb-16 rounded-2xl border border-[#e8eae3] bg-white p-6 md:p-7">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="font-serif-fact text-2xl text-[#1c2723]">
                  Kart Havuzuna Önerme Kat
                </h2>
                <p className="text-sm text-[#5c635e] mt-1">
                  Bir kategori seç, doğru ya da yanlış bir önerme gönder. Admin uygun bulursa kart havuzuna eklenir.
                </p>
              </div>
            </div>
            <form onSubmit={submitSuggestion} className="grid md:grid-cols-[1fr_1.8fr_auto] gap-3 items-start">
              <select
                value={suggestion.category_id}
                onChange={(e) => setSuggestion((s) => ({ ...s, category_id: e.target.value }))}
                className="h-11 rounded-xl border border-[#d6d9ce] bg-[#fdfdfb] px-3 text-sm text-[#1c2723]"
              >
                <option value="">Kategori seç</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <div className="space-y-2">
                <textarea
                  value={suggestion.text}
                  onChange={(e) => setSuggestion((s) => ({ ...s, text: e.target.value }))}
                  rows={3}
                  placeholder="Örn: Fiil ehliyeti için ayırt etme gücü şarttır."
                  className="w-full rounded-xl border border-[#d6d9ce] bg-[#fdfdfb] px-3 py-2 text-sm text-[#1c2723] resize-none"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSuggestion((s) => ({ ...s, is_true: true }))}
                    className={`h-9 px-4 rounded-full text-sm ${suggestion.is_true ? "bg-[#2e4733] text-white" : "border border-[#d6d9ce] text-[#5c635e]"}`}
                  >
                    Doğru önerme
                  </button>
                  <button
                    type="button"
                    onClick={() => setSuggestion((s) => ({ ...s, is_true: false }))}
                    className={`h-9 px-4 rounded-full text-sm ${!suggestion.is_true ? "bg-[#a55e3f] text-white" : "border border-[#d6d9ce] text-[#5c635e]"}`}
                  >
                    Yanlış önerme
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={suggestionLoading}
                className="h-11 px-5 rounded-full bg-[#2e4733] text-[#f5f6f2] hover:bg-[#1c2723] transition-colors text-sm font-medium disabled:opacity-50"
              >
                {suggestionLoading ? "Gönderiliyor..." : "Öner"}
              </button>
            </form>
            {suggestionStatus && (
              <div className="mt-3 text-sm text-[#5c635e]">{suggestionStatus}</div>
            )}
          </section>
        )}

        {/* Categories */}
        <section>
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-serif-fact text-2xl md:text-3xl text-[#1c2723]">
                {selectedGroup ? selectedGroup.name : "Ders Grupları"}
              </h2>
              {selectedGroup && (
                <p className="text-sm text-[#5c635e] mt-1">{selectedGroup.description}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {selectedGroup && (
                <button
                  type="button"
                  onClick={() => setSelectedGroupId(null)}
                  className="h-9 px-4 rounded-full border border-[#d6d9ce] text-sm text-[#5c635e] hover:bg-[#f5f6f2]"
                >
                  Gruplara dön
                </button>
              )}
              <span className="label-eyebrow">
                {selectedGroup ? `${selectedGroupCategories.length} ders` : `${categories.length} ders`}
              </span>
            </div>
          </div>

          {loading && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="categories-loading">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="h-40 rounded-2xl bg-[#ffffff] border border-[#e8eae3] animate-pulse"
                />
              ))}
            </div>
          )}

          {error && (
            <div
              className="rounded-2xl border border-red-200 bg-red-50 text-red-800 p-6"
              data-testid="categories-error"
            >
              {error}
            </div>
          )}

          {!loading && !error && categories.length === 0 && (
            <div
              className="rounded-2xl border border-[#e8eae3] bg-[#ffffff] p-10 text-center"
              data-testid="categories-empty"
            >
              <p className="text-[#5c635e]">
                Henüz kategori eklenmemiş. Yönetim panelinden ekleyebilirsiniz.
              </p>
            </div>
          )}

          {!loading && !error && categories.length > 0 && !selectedGroup && (
            <div className="grid md:grid-cols-3 gap-4" data-testid="category-groups-grid">
              {groupedCategories.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => setSelectedGroupId(group.id)}
                  className="text-left rounded-2xl border border-[#e8eae3] bg-white p-6 md:p-7 hover:border-[#4a6741]/40 hover:bg-[#fdfdfb] transition-all duration-300 fact-card-shadow"
                >
                  <div className="flex items-center justify-between gap-4 mb-5">
                    <div className="h-11 w-11 rounded-full bg-[#f5f6f2] border border-[#e8eae3] inline-flex items-center justify-center text-[#2e4733]">
                      <Layers size={18} />
                    </div>
                    <ArrowUpRight size={17} className="text-[#a3aba6]" />
                  </div>
                  <h3 className="font-serif-fact text-2xl text-[#1c2723] mb-2">
                    {group.name}
                  </h3>
                  <p className="text-sm text-[#5c635e] leading-relaxed min-h-12">
                    {group.description}
                  </p>
                  <div className="mt-5 flex items-center gap-3">
                    <span className="label-eyebrow text-[#2e4733]">{group.categories.length} ders</span>
                    <span className="label-eyebrow text-[#a3aba6]">{group.factCount} kart</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedGroup && (
          <div
            className="grid md:grid-cols-2 gap-4"
            data-testid="categories-grid"
          >
            {selectedGroupCategories.map((cat) => {
              const progress = getCategoryProgress(cat.id);
              const testProg = getTestProgress(cat.id);
              const learnedCount = progress.learned.length;
              const trueCount = cat.true_count ?? cat.fact_count;
              const totalAll = cat.fact_count;
              const pct = trueCount > 0 ? Math.round((learnedCount / trueCount) * 100) : 0;
              const wrongCount = testProg.wrong ? testProg.wrong.length : 0;
              return (
                <div
                  key={cat.id}
                  data-testid={`category-card-${cat.id}`}
                  className="group relative rounded-2xl border border-[#e8eae3] bg-[#ffffff] p-6 md:p-7 hover:border-[#4a6741]/40 transition-all duration-300 fact-card-shadow"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-3">
                        <BookOpenCheck size={14} className="text-[#4a6741]" />
                        <span className="label-eyebrow">
                          {trueCount} doğru bilgi · {cat.false_count ?? 0} yanlış cümle
                        </span>
                      </div>
                      <h3 className="font-serif-fact text-xl md:text-2xl text-[#1c2723] leading-snug mb-2">
                        {cat.name}
                      </h3>
                      {cat.description && (
                        <p className="text-sm text-[#5c635e] leading-relaxed line-clamp-2">
                          {cat.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="rounded-xl bg-[#f5f6f2] border border-[#e8eae3] px-4 py-3">
                      <div className="label-eyebrow">Çalışma</div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="font-serif-fact text-2xl text-[#1c2723]">
                          %{pct}
                        </span>
                        <span className="text-xs text-[#a3aba6]">
                          {learnedCount}/{trueCount}
                        </span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-[#f5f6f2] border border-[#e8eae3] px-4 py-3">
                      <div className="label-eyebrow">Test · en iyi</div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="font-serif-fact text-2xl text-[#1c2723]">
                          {testProg.bestScorePct != null ? `%${testProg.bestScorePct}` : "—"}
                        </span>
                        <span className="text-xs text-[#a3aba6]">
                          {totalAll} soru
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <Link
                      to={`/study/${cat.id}`}
                      data-testid={`go-study-${cat.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full bg-[#e8eae3] text-[#1c2723] hover:bg-[#d6d9ce] transition-colors text-sm font-medium"
                    >
                      <BookOpen size={14} />
                      Çalış
                    </Link>
                    <Link
                      to={`/test/${cat.id}`}
                      data-testid={`go-test-${cat.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full bg-[#2e4733] text-[#f5f6f2] hover:bg-[#1c2723] transition-colors text-sm font-medium group"
                    >
                      <ListChecks size={14} />
                      Test ol
                      <ArrowUpRight
                        size={14}
                        className="group-hover:rotate-45 transition-transform"
                      />
                    </Link>
                  </div>
                  {wrongCount > 0 && (
                    <Link
                      to={`/test/${cat.id}?mode=review`}
                      data-testid={`go-review-${cat.id}`}
                      className="mt-3 inline-flex items-center justify-center gap-2 w-full h-10 rounded-full border border-[#a55e3f]/30 text-[#a55e3f] hover:bg-[#a55e3f] hover:text-white hover:border-[#a55e3f] transition-colors text-sm font-medium"
                    >
                      <RotateCcw size={13} />
                      Yanlışlarımı tekrar dene ({wrongCount})
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </section>
        {suggestionBox}
      </main>

      <footer className="relative z-10 max-w-6xl mx-auto px-4 md:px-2 pb-10">
        <div className="border-t border-[#e8eae3] pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <span className="label-eyebrow text-[#a3aba6]">
            Oku · Tekrarla · Öğren
          </span>
          <span className="label-eyebrow text-[#a3aba6]">
            Av. A. Orkun Uğurlu © 2026
          </span>
        </div>
      </footer>
    </div>
  );
}
